import { useEffect, useState } from 'react';
import type { Pricing, TimeslotEntry, TimeslotGroup } from 'api/responseTypes';
import { useCartContext } from 'context/CartContext/CartContext';

/**
 * Encapsulates timeslot selection for the event/concert pages: tracks the chosen
 * slot's event key and pricing, and mirrors the slot's id/key plus its display
 * info (date/time/description) into the cart so the checkout summary can show the
 * selected slot instead of the parent event.
 *
 * Pass the parent event's key so the selection resets whenever the event changes.
 * This keeps different events (and different event types/cases) from conflicting
 * even when React Router reuses the page component across a `:eventKey` param
 * change instead of remounting it — a stale slot must never leak to another event.
 */
export const useTimeslotSelection = (parentEventKey?: string) => {
  const { setEventId, setEventKey, setTimeslot } = useCartContext();
  const [selectedSlotEventKey, setSelectedSlotEventKey] = useState<string | null>(null);
  const [selectedSlotPricing, setSelectedSlotPricing] = useState<Pricing[] | null>(null);

  // Reset the selection (local state + cart) on mount and whenever the parent
  // event changes, so a slot picked on one event never carries to another.
  useEffect(() => {
    setSelectedSlotEventKey(null);
    setSelectedSlotPricing(null);
    setTimeslot(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentEventKey]);

  const handleSlotSelect = (slot: TimeslotEntry, group: TimeslotGroup) => {
    setSelectedSlotEventKey(slot.eventKey);
    setEventId(slot.id);
    setEventKey(slot.eventKey);
    setSelectedSlotPricing(slot.pricing ?? null);
    setTimeslot({
      dateAndTime: slot.dateAndTime,
      time: slot.time,
      date: group.date,
      slotDescription: slot.slotDescription,
    });
  };

  return { selectedSlotEventKey, selectedSlotPricing, handleSlotSelect };
};
